import React from "react";
import { Link } from "react-router-dom";
import { Student } from "@/components/Global/DonutChart/DonutChart";
import { useTranslation } from "react-i18next";
import { StudentAvatar } from "../StudentAvatar/StudentAvatar";
import "./StudentListModal.scss";
import { TRACKING_LABELS, TRACKING_COLORS, TrackingStatusType } from "@/features/dashboard/utils/studentAssignmentsToTrackingStatus";
import { isCheckboxDisabled } from './utils/studentModaldisableCheckBox';

interface StudentListRowProps {
  student: Student;
  isSelected: boolean;
  toggleSelection: (studentId: string) => void;
  latestActivityId?: string | null;
  completedDate?: string;
  isCurrentPeriod: boolean;
  isCurrentOutOfDistrictPeriod: boolean;
  previousDistrictWindow: boolean;
  districtScreeningSetup?: boolean;
  selectedWindow?: {
    startDate: string | null;
    endDate: string | null;
    value: number | null;
    screeningWindowType: string | null;
    assignmentId: string | null;
  };
}

export const StudentListRow: React.FC<StudentListRowProps> = ({
  student,
  isSelected,
  toggleSelection,
  completedDate,
  latestActivityId,
  isCurrentPeriod,
  isCurrentOutOfDistrictPeriod,
  previousDistrictWindow,
  districtScreeningSetup,
  selectedWindow,
}) => {
  const { t } = useTranslation("dashboard");

  const formattedDate = student.completedDate
    ? new Date(student.completedDate).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })
    : completedDate
      ? new Date(completedDate).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })
      : null;

  const currentAssessmentStatus = student.assessmentStatus as TrackingStatusType;

  const checkboxDisabled = isCheckboxDisabled({
    districtScreeningSetup,
    selectedWindow,
    isCurrentPeriod,
    isCurrentOutOfDistrictPeriod,
    previousDistrictWindow
  });

  const handleCompletedAssessmentClick = () => {
    document.body.classList.remove('modal-open');
    const backdrop = document.querySelector('.modal-backdrop');
    if (backdrop) {
      backdrop.remove();
    }
  };

  return (
    <div className="student-list-row" key={student.studentId}>
      <div className={`checkbox-column${checkboxDisabled ? "-disabled" : ""}`}>
        <input
          type="checkbox"
          id={`student-${student.studentId}`}
          checked={isSelected}
          onChange={() => toggleSelection(student.studentId)}
          data-testid="student-checkbox"
          disabled={checkboxDisabled}
        />
      </div>
      <div className="name-column">
        <StudentAvatar firstName={student.firstName} lastName={student.lastName} />
        <span className="student-list-row-student-name">{student.firstName} {student.lastName}</span>
      </div>
      <div className="status-column">
        <div
          className="status-badge"
          style={{
            background: (currentAssessmentStatus === TrackingStatusType.ERROR || currentAssessmentStatus === TrackingStatusType.NEEDS_REVIEW)
              ? `repeating-linear-gradient(-45deg, #FF595E, #FF595E 6px, #F14668 6px, #F14668 7px)`
              : TRACKING_COLORS[currentAssessmentStatus],
          }}
        >
          {(currentAssessmentStatus === TrackingStatusType.DONE) && latestActivityId ? (
            <Link
              to={`/teacher/gradebook/scoring/${latestActivityId}`}
              className="status-link"
              onClick={handleCompletedAssessmentClick}
            >
              <span className="status-badge-text" data-testid="status-link">
                {t(TRACKING_LABELS[currentAssessmentStatus])}
                {formattedDate && (
                  <span className="completed-date">
                    {formattedDate}
                  </span>
                )}
              </span>
            </Link>
          ) : currentAssessmentStatus === TrackingStatusType.ERROR ? (
            <span
              className="status-badge-text action-needed"
              onClick={() => alert("schedule assessment flow")}
            >
              {t(TRACKING_LABELS[currentAssessmentStatus])}
            </span>
          ) : (
            <span
              className="status-badge-text"
            >
              {t(TRACKING_LABELS[currentAssessmentStatus])}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};